import ReportShell from "./ReportShell";
import { money, qty, percent, QUARTERS, categoryMeals } from "@/lib/poshanConstants";
import { inWordsEN, inWordsOR } from "@/lib/numberToWords";

export function SummaryReport({ schoolInfo, totals, monthlySummaries, months }) {
  const quarterLabel = QUARTERS[schoolInfo.quarter].label;
  return (
    <ReportShell title="Quarterly Financial Summary" subtitle={`${quarterLabel} | FY ${schoolInfo.financialYear}`}>
      <div className="mb-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div><b>School:</b> {schoolInfo.schoolName || "____________"}</div>
        <div><b>UDISE:</b> {schoolInfo.udiseCode || "____________"}</div>
        <div><b>District:</b> {schoolInfo.district || "____________"}</div>
        <div><b>Block:</b> {schoolInfo.block || "____________"}</div>
      </div>
      <table className="report-table mb-5 w-full">
        <tbody>
          <tr><th>Total Grant Received</th><td>{money(totals.totalGrantReceived)}</td><th>Total Cooking Cost</th><td>{money(totals.totalCookingCost)}</td></tr>
          <tr><th>Total Ingredient Expenditure</th><td>{money(totals.totalIngredientExpenditure)}</td><th>Total Cook Payment</th><td>{money(totals.totalCookPayment)}</td></tr>
          <tr><th>UC Utilized Amount</th><td>{money(totals.ucUtilizedAmount)}</td><th>Total Remaining Balance</th><td>{money(totals.remainingBalance)}</td></tr>
          <tr><th>Cook Cum Helper Payment</th><td>{money(totals.totalCookPayment)}</td><th>Total Cash Outgo</th><td>{money(totals.totalExpenditure)}</td></tr>
          <tr><th>Total Rice Consumption</th><td>{qty(totals.totalRiceConsumption)} kg</td><th>Total Meals Served</th><td>{qty(totals.totalMealsServed)}</td></tr>
        </tbody>
      </table>
      <table className="report-table w-full">
        <thead>
          <tr><th>Month</th><th>Grant Received</th><th>Cooking Cost</th><th>Bal Vatika Meals</th><th>Primary Meals</th><th>Upper Primary Meals</th><th>Total Meals</th><th>Rice Used</th></tr>
        </thead>
        <tbody>
          {monthlySummaries.map((s, idx) => {
            const cm = categoryMeals(months[idx]);
            return (
              <tr key={s.monthName}>
                <td>{s.monthName}</td>
                <td>{money(s.fundReceived)}</td>
                <td>{money(s.fundExpenditure)}</td>
                <td>{qty(cm[0].meals)}</td>
                <td>{qty(cm[1].meals)}</td>
                <td>{qty(cm[2].meals)}</td>
                <td>{qty(s.mealsServed)}</td>
                <td>{qty(s.riceConsumption)} kg</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ReportShell>
  );
}

export function HandReceiptReport({ schoolInfo, totals }) {
  const quarterLabel = QUARTERS[schoolInfo.quarter].label;
  return (
    <ReportShell title="Quarterly Hand Receipt" subtitle="PM POSHAN / MDM Grant Receipt and Utilization Statement">
      <div className="space-y-4 text-base leading-7">
        <p>
          Received and utilized PM POSHAN grant for <b>{schoolInfo.schoolName || "____________"}</b>, UDISE <b>{schoolInfo.udiseCode || "____________"}</b>,
          Village <b>{schoolInfo.village || "____________"}</b>, Block <b>{schoolInfo.block || "____________"}</b>, District <b>{schoolInfo.district || "____________"}</b>.
        </p>
        <table className="report-table w-full">
          <tbody>
            <tr><th>Quarter</th><td>{quarterLabel}</td><th>Financial Year</th><td>{schoolInfo.financialYear}</td></tr>
            <tr><th>Total Grant Received</th><td>{money(totals.totalGrantReceived)}</td><th>UC Utilized Amount</th><td>{money(totals.ucUtilizedAmount)}</td></tr>
            <tr><th>Remaining Balance</th><td>{money(totals.remainingBalance)}</td><th>Date</th><td>{schoolInfo.reportDate || "____________"}</td></tr>
          </tbody>
        </table>
        <div className="rounded border border-black/30 bg-sand-50 px-4 py-3 text-sm leading-6" data-testid="hand-receipt-amount-words">
          <p><b>Amount in words (English):</b> {inWordsEN(totals.ucUtilizedAmount)}</p>
          <p className="mt-1"><b>ଅକ୍ଷରରେ (ଓଡ଼ିଆ):</b> {inWordsOR(totals.ucUtilizedAmount)}</p>
        </div>
        <p>
          Certified that the above amount has been used for the purpose of PM POSHAN meals and related school level expenditure during the selected quarter.
        </p>
      </div>
      <div className="mt-20 grid grid-cols-2 gap-12 text-center text-sm">
        <div className="border-t border-black pt-2">Seal and Signature of SMC/SMDC Chairperson</div>
        <div className="border-t border-black pt-2">Seal and Signature of Head Teacher / I.C. HM</div>
      </div>
    </ReportShell>
  );
}

export function OdiaReceiptsReport({ schoolInfo, odiaReceiptGroups }) {
  return (
    <>
      {odiaReceiptGroups.map((group) => {
        const blankRows = Array.from({ length: Math.max(0, 8 - group.items.length) });
        const isLadooMaking = group.key.startsWith("ladooMaking");
        return (
          <ReportShell
            key={group.key}
            title={group.title}
            subtitle={`${group.period} | ${schoolInfo.financialYear}`}
            className="odia-receipt-page"
          >
            <div className="odia-receipt text-base leading-8">
              <div className="flex items-center justify-between text-sm" data-testid={`odia-receipt-meta-${group.key}`}>
                <div><b>Voucher No:</b> {(group.items[0] && group.items[0].voucher) || "________"}</div>
                <div><b>Date / ତାରିଖ:</b> {(group.items[0] && group.items[0].date) || `${group.period} ${schoolInfo.financialYear}`}</div>
              </div>
              <p className="mt-2 text-center font-bold underline">ହାତ ରସିଦ</p>
              {(() => {
                // Odia-first rendering with English fallback — if a CCH/vendor
                // master record has no Odia value for a field, fall back to the
                // English value entered by the user. Block / district fall back
                // to schoolInfo when both payee Odia & English are empty.
                const pick = (odia, eng) => {
                  const o = typeof odia === "string" ? odia.trim() : "";
                  if (o) return o;
                  const e = typeof eng === "string" ? eng.trim() : "";
                  return e;
                };
                const payeeName = isLadooMaking
                  ? pick(group.cchNameOdia, group.cchName)
                  : pick(group.vendorNameOdia, group.vendorName);
                // iter124 — agency entities drop the father line and instead
                // print the Agency Name as a separate honorific line.
                const payeeIsAgency = isLadooMaking
                  ? (group.cchEntityType === "agency")
                  : (group.vendorEntityType === "agency");
                const payeeAgencyNameOdia = isLadooMaking
                  ? (group.cchAgencyNameOdia || "")
                  : (group.vendorAgencyNameOdia || "");
                const payeeFather = isLadooMaking ? pick(group.cchFatherOdia, group.cchFather) : pick(group.vendorFatherOdia, group.vendorFather);
                // iter153 — Honorific depends on the record's relationType.
                const payeeRelation = isLadooMaking ? (group.cchRelation || "father") : (group.vendorRelation || "father");
                const payeeRelationLabelOdia = payeeRelation === "husband" ? "ସ୍ୱାମୀ" : "ପିତା";
                const payeeVillage = isLadooMaking ? pick(group.cchVillageOdia, group.cchVillage) : pick(group.vendorVillageOdia, group.vendorVillage);
                const payeePost = isLadooMaking ? pick(group.cchPostOdia, group.cchPost) : pick(group.vendorPostOdia, group.vendorPost);
                const payeeThana = isLadooMaking ? pick(group.cchThanaOdia, group.cchThana) : pick(group.vendorThanaOdia, group.vendorThana);
                const payeeBlock = isLadooMaking ? pick(group.cchBlockOdia, group.cchBlock) : pick(group.vendorBlockOdia, group.vendorBlock);
                const payeeDistrict = isLadooMaking ? pick(group.cchDistrictOdia, group.cchDistrict) : pick(group.vendorDistrictOdia, group.vendorDistrict);
                const payeePincode = isLadooMaking ? pick(group.cchPincodeOdia, group.cchPincode) : pick(group.vendorPincodeOdia, group.vendorPincode);
                const schoolDisplayName = pick(schoolInfo.schoolNameOdia, schoolInfo.schoolName);
                const blockFallback = pick(schoolInfo.blockOdia, schoolInfo.block);
                const districtFallback = pick(schoolInfo.districtOdia, schoolInfo.district);
                const blank = "................................";
                return (
                  <div className="mt-4 leading-9" data-testid={`odia-receipt-intro-${group.key}`}>
                    {payeeIsAgency ? (
                      <p className="receipt-intro-justify" data-testid={`odia-receipt-agency-intro-${group.key}`}>
                        ମୁଁ <b>{payeeName || blank}</b>{payeeAgencyNameOdia ? <>, <b>{payeeAgencyNameOdia}</b> ସଂସ୍ଥା / Self-Help Group ର ପକ୍ଷରୁ</> : null}, ଗ୍ରାମ: <b>{payeeVillage || blank}</b>, ପୋଷ୍ଟ: <b>{payeePost || blank}</b>, ଥାନା: <b>{payeeThana || blank}</b>, ବ୍ଲକ: <b>{payeeBlock || blockFallback || blank}</b>, ଜିଲ୍ଲା: <b>{payeeDistrict || districtFallback || blank}</b>, ପିନକୋଡ଼: <b>{payeePincode || blank}</b> ।
                      </p>
                    ) : (
                      <p className="receipt-intro-justify">
                        ମୁଁ <b>{payeeName || blank}</b>, {payeeRelationLabelOdia}: <b>{payeeFather || blank}</b>, ଗ୍ରାମ: <b>{payeeVillage || blank}</b>, ପୋଷ୍ଟ: <b>{payeePost || blank}</b>, ଥାନା: <b>{payeeThana || blank}</b>, ବ୍ଲକ: <b>{payeeBlock || blockFallback || blank}</b>, ଜିଲ୍ଲା: <b>{payeeDistrict || districtFallback || blank}</b>, ପିନକୋଡ଼: <b>{payeePincode || blank}</b> ।
                      </p>
                    )}
                    <p className="mt-3 receipt-intro-justify">
                      ଏହି ହାତ ରସିଦ ଲେଖିଦେବା କାରଣ ଏହିକି ଯେ, <b>{schoolDisplayName || blank}</b> ବିଦ୍ୟାଳୟର ମଧ୍ୟାହ୍ନ ଭୋଜନ (MDM) ନିମନ୍ତେ <b>{group.period || blank}</b> ମାସ ପାଇଁ ନିମ୍ନ ପ୍ରକାରେ {isLadooMaking ? "ସେବା" : "ଜିନିଷ"} ଯୋଗାଇ ଦେଇଅଛି ।
                    </p>
                    <p className="mt-2 font-semibold">
                      ଯୋଗାଇ ଦିଆଯାଇଥିବା {isLadooMaking ? "ସେବାର" : "ଜିନିଷର"} ବିବରଣୀ:
                    </p>
                  </div>
                );
              })()}
              <table className="report-table mt-4 w-full">
                <thead>
                  <tr>
                    <th>ଦ୍ରବ୍ୟ</th><th>ପରିମାଣ</th><th>ଦର</th><th>ମୋଟ ମୂଲ୍ୟ</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item, i) => (
                    <tr key={`${group.key}-${i}-${item.name}`}>
                      <td>{item.name}</td>
                      <td>{qty(item.quantity)} {item.unit}</td>
                      <td>{money(item.rate)}</td>
                      <td>{money(item.amount)}</td>
                    </tr>
                  ))}
                  {group.items.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center">ଏହି ବର୍ଗରେ ଖର୍ଚ୍ଚ ତଥ୍ୟ ନାହିଁ ।</td>
                    </tr>
                  )}
                  {blankRows.map((_, i) => (
                    <tr key={`${group.key}-blank-${i}`}>
                      <td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td>
                    </tr>
                  ))}
                  <tr>
                    <th colSpan={3} className="text-right">Total / ମୋଟ</th>
                    <th>{money(group.total)}</th>
                  </tr>
                </tbody>
              </table>
              <p className="mt-5" data-testid={`odia-receipt-words-${group.key}`}>
                ସର୍ବମୋଟ ଟ. <b>{money(group.total)}</b> ଅକ୍ଷରରେ <b>{inWordsOR(group.total)}</b> ।
              </p>
              <p className="text-sm italic text-ink-700">
                <b>In words (English):</b> {inWordsEN(group.total)}
              </p>
              {group.note && <p className="font-semibold">{group.note}</p>}
              <p className="mt-3 leading-8" data-testid={`odia-receipt-acknowledgement-${group.key}`}>
                ଉପରୋକ୍ତ ସମସ୍ତ ଟଙ୍କା ମୁଁ <b>{(group.cchNameOdia || group.vendorNameOdia || group.cchName || group.vendorName) || "................................................"}</b>, <b>{schoolInfo.schoolNameOdia || schoolInfo.schoolName || "................................................"}</b> ବିଦ୍ୟାଳୟର ପ୍ରଧାନଶିକ୍ଷକ / ଶିକ୍ଷୟିତ୍ରୀଙ୍କ ଠାରୁ ନଗଦ ବୁଝି ପାଇଲି । ଏହା ଲେଖିଦେଲି କି ଯେପରି ଦରକାର ବେଳେ ଏହା କାମରେ ଆସିବ ।
              </p>
              <div className="mt-12 grid grid-cols-2 gap-10 text-sm">
                <div>Passed Rs. __________________________________ Only.</div>
                <div className="text-right">ସ୍ୱାକ୍ଷର</div>
                <div>Paid by me,</div>
                <div />
                <div className="mt-12 border-t border-black pt-2 text-center">Headmaster</div>
                <div className="mt-12 border-t border-black pt-2 text-center">Chairperson</div>
              </div>
            </div>
          </ReportShell>
        );
      })}
    </>
  );
}

export function Ogfr23Report({ schoolInfo, totals, ucScope }) {
  const scope = ucScope || { mode: "quarterly", label: QUARTERS[schoolInfo.quarter].label, periodNote: "Quarterly UC" };
  const previousBalance =
    schoolInfo.previousUnspentBalance - schoolInfo.previousUtilizedAmount > 0
      ? schoolInfo.previousUnspentBalance - schoolInfo.previousUtilizedAmount
      : 0;
  return (
    <ReportShell title="FORM OGFR-23" subtitle={`[See Rule 306(iii)(a)] Form of Utilization Certificate — ${scope.label}`} className="uc-page uc-page-compact">
      <div className="space-y-2 text-base leading-snug">
        <p className="text-center text-base font-bold underline">
          Name of the Department - {schoolInfo.departmentName || "School & Mass Education Department"}
        </p>
        <p>
          <b>Name of the School:</b> {schoolInfo.schoolName || "____________"} &nbsp; <b>U.Dise:</b> {schoolInfo.udiseCode || "____________"} &nbsp;
          <b>Grant:</b> {schoolInfo.schemeName || "PM POSHAN"} &nbsp; <b>Year:</b> {schoolInfo.financialYear} &nbsp; <b>Period:</b> {scope.label}
        </p>
        <p>
          1. Certified that a sum of <b>{money(totals.ucUtilizedAmount)}</b> has been utilized under the scheme <b>{schoolInfo.schemeName || "PM POSHAN"}</b> by
          <b> {schoolInfo.schoolName || "the grantee school"}</b> out of grant-in-aid of <b>{money(totals.totalGrantReceived)}</b> sanctioned by the Department during the financial year <b>{schoolInfo.financialYear}</b> as indicated below in Table-1.
        </p>
        <p>
          2. Out of <b>{money(schoolInfo.previousUnspentBalance)}</b> remaining unspent balance of the previous years, a sum of <b>{money(schoolInfo.previousUtilizedAmount)}</b> has been utilized as indicated below in Table-2.
        </p>
        <p>
          3. The utilization has been made for the purpose for which it was sanctioned and that a balance of <b>{money(totals.currentYearUcBalance)}</b> remaining unutilized at the end of the {scope.mode === "monthly" ? "month" : "quarter"} will be carried forward or adjusted as per Government instructions.
        </p>
      </div>

      <h4 className="text-center font-bold underline uc-tight">Table-1 (Details of Current Year)</h4>
      <table className="report-table w-full">
        <thead>
          <tr>
            <th>Sl. No</th><th>Sanction No &amp; Date</th><th>Sanctioned Amount</th><th>Adj. towards GIA/Loan</th><th>Amount utilized</th><th>Deposited back</th><th>Balance</th><th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td><td>{schoolInfo.sanctionNoDate || `${scope.label} PM POSHAN Grant`}</td><td>{money(totals.totalGrantReceived)}</td><td>{money(0)}</td><td>{money(totals.ucUtilizedAmount)}</td><td>{money(0)}</td><td>{money(totals.currentYearUcBalance)}</td><td>{scope.periodNote || (scope.mode === "monthly" ? "Monthly UC" : "Quarterly UC")}</td>
          </tr>
          <tr><th colSpan={2}>Total</th><th>{money(totals.totalGrantReceived)}</th><th>{money(0)}</th><th>{money(totals.ucUtilizedAmount)}</th><th>{money(0)}</th><th>{money(totals.currentYearUcBalance)}</th><th /></tr>
        </tbody>
      </table>

      <h4 className="text-center font-bold underline uc-tight">Table-2 (Details of Previous Year)</h4>
      <table className="report-table w-full">
        <thead>
          <tr>
            <th>Sl. No</th><th>Sanction No &amp; Date</th><th>Sanctioned Amount</th><th>Unspent at close of prev. year</th><th>Amount utilized</th><th>Deposited back</th><th>Adj. towards GIA/Loan</th><th>Balance</th><th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td><td>{schoolInfo.previousSanctionNoDate || "Optional"}</td><td>{money(schoolInfo.previousSanctionedAmount)}</td><td>{money(schoolInfo.previousUnspentBalance)}</td><td>{money(schoolInfo.previousUtilizedAmount)}</td><td>{money(0)}</td><td>{money(0)}</td><td>{money(previousBalance)}</td><td>Previous year entry</td>
          </tr>
          <tr><th colSpan={2}>Total</th><th>{money(schoolInfo.previousSanctionedAmount)}</th><th>{money(schoolInfo.previousUnspentBalance)}</th><th>{money(schoolInfo.previousUtilizedAmount)}</th><th>{money(0)}</th><th>{money(0)}</th><th>{money(previousBalance)}</th><th /></tr>
        </tbody>
      </table>

      <p className="uc-tight">
        4. Certified that I have satisfied myself that the conditions on which the grants-in-aid was sanctioned have been duly fulfilled and that I have exercised the checks to see that the money was actually utilized for the purpose for which it was sanctioned.
      </p>
      <div className="uc-tight">
        <span className="font-bold underline">Kinds of checks exercised:</span>
        &nbsp;1. Cash book, voucher and stock register verified.
        &nbsp;2. Monthly PM POSHAN data checked with school records.
        &nbsp;3. Grant utilization checked against approved PM POSHAN expenditure records.
      </div>
      <div className="uc-sig-row grid grid-cols-2 gap-12 text-center text-xs">
        <div className="border-t border-black pt-1">Seal and Sign of SMC/SMDC Chairperson&nbsp;&nbsp;&nbsp;Date</div>
        <div className="border-t border-black pt-1">Seal and Sign of HOD/HM/I.C.HM&nbsp;&nbsp;&nbsp;Date</div>
      </div>
    </ReportShell>
  );
}

export function Ogfr24Report({ schoolInfo, totals, ucScope, annexureEntries }) {
  const scope = ucScope || { mode: "quarterly", label: "Quarterly", periodNote: "Quarterly UC" };
  const annex = annexureEntries || [];
  const grandTotal = annex.reduce((s, e) => s + e.amount, 0);
  return (
    <ReportShell title="FORM OGFR-24 + ANNEXURE-1" subtitle={`[See Rule 306(iii)(b)] Physical Target / Achievement — ${scope.label}`} className="uc-page uc-page-compact">
      <table className="report-table w-full text-xs">
        <thead>
          <tr>
            <th>School</th><th>Scheme</th><th>Financial Target</th><th>GIA Received</th><th>Physical Target</th><th>Amount Utilized</th><th>Furnished Previously</th><th>Achievement vs prior UC</th><th>Achievement vs present UC</th><th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{schoolInfo.schoolName || "____________"}</td><td>{schoolInfo.schemeName || "PM POSHAN"}</td><td>{money(totals.totalGrantReceived)}</td><td>{money(totals.totalGrantReceived)}</td><td>{qty(totals.physicalTarget)} meals</td><td>{money(totals.ucUtilizedAmount)}</td><td>{money(0)}</td><td>0</td><td>{qty(totals.totalMealsServed)} meals</td><td>Util. {percent(totals.utilizationPercentage)}, Achv. {percent(totals.achievementPercentage)}</td>
          </tr>
        </tbody>
      </table>
      <p className="uc-tight italic">
        Reasons for non-utilization of grant-in-aid and shortfall in achieving the target should be explained in the remarks column where applicable.
      </p>

      <h4 className="text-center font-bold uppercase underline uc-tight">Physical Target Achievement Report</h4>
      <table className="report-table w-full">
        <tbody>
          <tr><th>Financial Target</th><td>{money(totals.totalGrantReceived)}</td><th>Grant Received</th><td>{money(totals.totalGrantReceived)}</td></tr>
          <tr><th>Utilized Amount for UC</th><td>{money(totals.ucUtilizedAmount)}</td><th>Meals Served</th><td>{qty(totals.totalMealsServed)}</td></tr>
          <tr><th>Students Benefited</th><td>{qty(totals.studentsBenefited)}</td><th>Physical Target</th><td>{qty(totals.physicalTarget)} meals</td></tr>
          <tr><th>Utilization Percentage</th><td>{percent(totals.utilizationPercentage)}</td><th>Achievement Percentage</th><td>{percent(totals.achievementPercentage)}</td></tr>
        </tbody>
      </table>

      <h4 className="text-center font-bold underline uc-tight">ANNEXURE-1 — Statement of Expenditure</h4>
      <table className="report-table w-full">
        <thead>
          <tr><th>Voucher</th><th>Date</th><th>Details of Expenditure</th><th>Amount</th><th>Remarks</th></tr>
        </thead>
        <tbody>
          {annex.length === 0 && <tr><td colSpan={5} className="text-center">No expenditure entered yet.</td></tr>}
          {annex.map((e) => (
            <tr key={`${e.voucher}-${e.details}`}>
              <td>{e.voucher}</td><td>{e.date}</td><td>{e.details}</td><td>{money(e.amount)}</td><td>{e.remarks}</td>
            </tr>
          ))}
          <tr><th colSpan={3}>GRAND TOTAL</th><th>{money(grandTotal)}</th><th /></tr>
        </tbody>
      </table>
      <p className="uc-tight">
        <b>UNDERTAKING:</b> We the undersigned undertake that the statement of expenditure information furnished above has been spent by {schoolInfo.schoolName || "____________"}, Block {schoolInfo.block || "____________"}, District {schoolInfo.district || "____________"} during the financial year {schoolInfo.financialYear} ({scope.label}) and all original vouchers are kept at the school for verification by authorized persons.
      </p>
      <div className="uc-sig-row grid grid-cols-2 gap-12 text-center text-xs">
        <div className="border-t border-black pt-1">Seal and Sign of SMC/SMDC Chairperson&nbsp;&nbsp;&nbsp;Date</div>
        <div className="border-t border-black pt-1">Seal and Sign of HOD/HM/I.C.HM&nbsp;&nbsp;&nbsp;Date</div>
      </div>
    </ReportShell>
  );
}

export function AnnexureReport({ schoolInfo, annexureEntries }) {
  return (
    <ReportShell title="ANNEXURE-1" subtitle="Statement of Expenditure Details of Form OGFR-23 & 24">
      <table className="report-table w-full">
        <thead>
          <tr><th>Voucher No.</th><th>Date</th><th>Details of Expenditure</th><th>Amount</th><th>Remarks</th></tr>
        </thead>
        <tbody>
          {annexureEntries.length === 0 && <tr><td colSpan={5} className="text-center">No expenditure entered yet.</td></tr>}
          {annexureEntries.map((e) => (
            <tr key={`${e.voucher}-${e.details}`}>
              <td>{e.voucher}</td><td>{e.date}</td><td>{e.details}</td><td>{money(e.amount)}</td><td>{e.remarks}</td>
            </tr>
          ))}
          <tr><th colSpan={3}>GRAND TOTAL</th><th>{money(annexureEntries.reduce((s, e) => s + e.amount, 0))}</th><th /></tr>
        </tbody>
      </table>
      <h4 className="mt-8 text-center text-lg font-bold underline">UNDERTAKING</h4>
      <p className="mt-3 text-base leading-7">
        We the undersigned undertake that the statement of expenditure information furnished above has been spent by {schoolInfo.schoolName || "____________"}, Block {schoolInfo.block || "____________"}, District {schoolInfo.district || "____________"} during the financial year {schoolInfo.financialYear} and all original vouchers are kept at the school for verification by authorized persons.
      </p>
      <div className="mt-16 grid grid-cols-2 gap-12 text-center text-sm">
        <div className="border-t border-black pt-2">Seal and Sign of SMC/SMDC Chairperson<br />Date</div>
        <div className="border-t border-black pt-2">Seal and Sign of HOD/HM/I.C.HM<br />Date</div>
      </div>
    </ReportShell>
  );
}

export function IngredientReport({ schoolInfo, ingredientWiseReport, totals }) {
  const quarterLabel = QUARTERS[schoolInfo.quarter].label;
  return (
    <ReportShell title="Ingredient-wise Expenditure Report" subtitle={`${quarterLabel} | FY ${schoolInfo.financialYear}`}>
      <table className="report-table w-full">
        <thead>
          <tr><th>Sl No</th><th>Ingredient Name</th><th>Quantity</th><th>Unit</th><th>Total Amount</th></tr>
        </thead>
        <tbody>
          {ingredientWiseReport.length === 0 && <tr><td colSpan={5} className="text-center">No ingredient expenditure entered yet.</td></tr>}
          {ingredientWiseReport.map((item, index) => (
            <tr key={item.name}>
              <td>{index + 1}</td><td>{item.name}</td><td>{qty(item.quantity)}</td><td>{item.unit}</td><td>{money(item.amount)}</td>
            </tr>
          ))}
          <tr><th colSpan={4}>Total</th><th>{money(totals.totalIngredientExpenditure)}</th></tr>
        </tbody>
      </table>
    </ReportShell>
  );
}

export default function Reports({
  activeReport,
  schoolInfo,
  totals,
  monthlySummaries,
  months,
  odiaReceiptGroups,
  annexureEntries,
  ingredientWiseReport,
  dryRationData,
  ucScope,
}) {
  if (activeReport === "all") {
    return (
      <>
        <SummaryReport schoolInfo={schoolInfo} totals={totals} monthlySummaries={monthlySummaries} months={months} />
        <HandReceiptReport schoolInfo={schoolInfo} totals={totals} />
        <OdiaReceiptsReport schoolInfo={schoolInfo} odiaReceiptGroups={odiaReceiptGroups} />
        <DryRationReport schoolInfo={schoolInfo} dryRationData={dryRationData} />
        <FsaRiceDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />
        <FsaCostDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />
        <Ogfr23Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} />
        <Ogfr24Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} annexureEntries={annexureEntries} />
        <AnnexureReport schoolInfo={schoolInfo} annexureEntries={annexureEntries} />
        <IngredientReport schoolInfo={schoolInfo} ingredientWiseReport={ingredientWiseReport} totals={totals} />
      </>
    );
  }
  switch (activeReport) {
    case "summary": return <SummaryReport schoolInfo={schoolInfo} totals={totals} monthlySummaries={monthlySummaries} months={months} />;
    case "handReceipt": return <HandReceiptReport schoolInfo={schoolInfo} totals={totals} />;
    case "odiaReceipts": return <OdiaReceiptsReport schoolInfo={schoolInfo} odiaReceiptGroups={odiaReceiptGroups} />;
    case "dryRation": return <DryRationReport schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "fsaRice": return <FsaRiceDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "fsaCost": return <FsaCostDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "ogfr23": return <Ogfr23Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} />;
    case "ogfr24": return <Ogfr24Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} annexureEntries={annexureEntries} />;
    case "annexure": return <AnnexureReport schoolInfo={schoolInfo} annexureEntries={annexureEntries} />;
    case "ingredient": return <IngredientReport schoolInfo={schoolInfo} ingredientWiseReport={ingredientWiseReport} totals={totals} />;
    default: return null;
  }
}

// =====================================================================
// Dry Ration detailed report — lists every dry-ration day with classwise
// rice qty & cooking-cost, totals + grand totals at bottom. Each day also
// gets its own printable Distribution Slip (parent acknowledgement form).
// =====================================================================
function DryRationSlip({ schoolInfo, day }) {
  const blank = "................................";
  const cls = [
    { key: "balVatika", label: "Bal Vatika" },
    { key: "primary", label: "Primary (1-5)" },
    { key: "upperPrimary", label: "Upper Primary (6-8)" },
    { key: "highSchool", label: "High School (9-10)" },
  ];
  return (
    <section className="print-page mt-8 break-before-page" data-testid={`dr-slip-${day.date}`}>
      <div className="dry-ration-slip space-y-3 p-2">
        <div className="flex items-baseline justify-between border-b border-black pb-2">
          <h3 className="text-xl font-bold uppercase tracking-wide">Dry Ration Distribution Slip</h3>
          <p className="text-sm">Date: <b>{day.date}</b></p>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <p>School: <b>{schoolInfo.schoolName || blank}</b></p>
          <p>UDISE: <b>{schoolInfo.udiseCode || blank}</b></p>
          <p>Block: <b>{schoolInfo.block || blank}</b></p>
          <p>District: <b>{schoolInfo.district || blank}</b></p>
        </div>
        <p className="mt-2 text-sm">
          The school has distributed Dry Ration (Rice + Cooking Cost in cash / kind) to enrolled students for the date <b>{day.date}</b> as per the PM POSHAN guidelines.
          The classwise quantities and cost values below match the ration handed over to parents / guardians.
        </p>
        <table className="report-table mt-2 w-full text-sm">
          <thead>
            <tr>
              <th>Class</th>
              <th>Enrolled Students</th>
              <th>Rice (kg)</th>
              <th>Cooking Cost (Rs.)</th>
              <th>Parent / Guardian Signature</th>
            </tr>
          </thead>
          <tbody>
            {cls.map((c) => (
              <tr key={c.key}>
                <td>{c.label}</td>
                <td className="text-center">{day[c.key].count || 0}</td>
                <td className="text-center">{qty(day[c.key].rice)} kg</td>
                <td className="text-center">{money(day[c.key].cost)}</td>
                <td>&nbsp;</td>
              </tr>
            ))}
            <tr className="bg-sand-100 font-semibold">
              <td colSpan={2} className="text-right">Day Total</td>
              <td className="text-center">{qty(day.totals.rice)} kg</td>
              <td className="text-center">{money(day.totals.cost)}</td>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </table>
        <div className="rounded border border-black/30 bg-sand-50 px-3 py-2 text-sm">
          <p><b>Amount in words (English):</b> {inWordsEN(day.totals.cost)}</p>
          <p className="mt-1"><b>ଅକ୍ଷରରେ (ଓଡ଼ିଆ):</b> {inWordsOR(day.totals.cost)}</p>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-8 text-center text-xs">
          <div className="border-t border-black pt-2">
            Signature of Distributing Officer<br />
            (Head Teacher / SMC Member)
          </div>
          <div className="border-t border-black pt-2">
            Counter-signature of Block Education Officer<br />
            (BEO) / Designated Authority
          </div>
        </div>
      </div>
    </section>
  );
}

export function DryRationReport({ schoolInfo, dryRationData }) {
  const days = (dryRationData && dryRationData.days) || [];
  const totals = (dryRationData && dryRationData.totals) || {
    days: 0, balVatika: { rice: 0, cost: 0 }, primary: { rice: 0, cost: 0 },
    upperPrimary: { rice: 0, cost: 0 }, highSchool: { rice: 0, cost: 0 },
    grandRice: 0, grandCost: 0,
  };

  // Period summary — classwise breakdown using PM-POSHAN per-student per-day
  // rates. Total rice / cost for the period is derived as
  //   days × ratePerStudentPerDay × Σstudents-served-across-days.
  // We aggregate class counts across all DR days so the "students" column
  // reflects the actual headcount fed (e.g. 9 days × 5 BV students/day =
  // 45 student-days for BV → 45 × 100g = 4500g rice).
  const sumCount = (k) => days.reduce((acc, d) => acc + (d?.[k]?.count || 0), 0);
  const summary = [
    { key: "balVatika",    label: "Bal Vatika",    odia: "ବାଳ ବାଟିକା",         ricePerStuG: 100, costPerStu: 11.15, studentDays: sumCount("balVatika"),    totalRice: totals.balVatika.rice,    totalCost: totals.balVatika.cost },
    { key: "primary",      label: "Primary",       odia: "ପ୍ରାଥମିକ",           ricePerStuG: 100, costPerStu: 11.15, studentDays: sumCount("primary"),      totalRice: totals.primary.rice,      totalCost: totals.primary.cost },
    { key: "upperPrimary", label: "Upper Primary", odia: "ଉଚ୍ଚ ପ୍ରାଥମିକ",     ricePerStuG: 150, costPerStu: 14.74, studentDays: sumCount("upperPrimary"), totalRice: totals.upperPrimary.rice, totalCost: totals.upperPrimary.cost },
    { key: "highSchool",   label: "High School",   odia: "ଉଚ୍ଚ ବିଦ୍ୟାଳୟ",     ricePerStuG: 150, costPerStu: 14.74, studentDays: sumCount("highSchool"),   totalRice: totals.highSchool.rice,   totalCost: totals.highSchool.cost },
  ].map((r) => (r.studentDays > 0
    // Show the rate the totals actually used (rate card / report rule), not a fixed default.
    ? { ...r, ricePerStuG: Number(((r.totalRice * 1000) / r.studentDays).toFixed(1)), costPerStu: Number((r.totalCost / r.studentDays).toFixed(2)) }
    : r));

  return (
    <ReportShell
      title="Dry Ration Report"
      subtitle={`${schoolInfo.financialYear} · ${schoolInfo.quarter}`}
      testId="dry-ration-report"
    >
      <div className="dry-ration-report space-y-4 text-sm" data-testid="dry-ration-content">
        <p className="text-ink-700">
          Quantity of rice (kg) and cooking cost (Rs.) distributed to enrolled students on each dry-ration day, classwise.
          Total dry-ration days in the selected quarter: <b>{totals.days}</b>.
        </p>

        {/* Period summary — the classwise headline numbers user-explained
            "9 days × 100 g BV × 5 students = 4500 g". One row per class. */}
        <div className="rounded border border-clay/40 bg-clay/5 p-3" data-testid="dr-period-summary">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-clay-dark">
            Dry-Ration Period Summary · ଦ୍ରବ୍ୟ ବଣ୍ଟନ ସମୟ ସାରାଂଶ
          </p>
          <table className="report-table w-full">
            <thead>
              <tr>
                <th>Class · ଶ୍ରେଣୀ</th>
                <th>DR Days × Students<br/><span className="text-[0.65rem] font-normal">(student-days)</span></th>
                <th>Rice / student / day</th>
                <th>Total Rice<br/>distributed</th>
                <th>Cost / student / day</th>
                <th>Total Cooking Cost<br/>distributed</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.key} data-testid={`dr-summary-${row.key}`}>
                  <td><b>{row.label}</b><br/><span className="text-xs text-ink-700">{row.odia}</span></td>
                  <td className="text-center font-mono">{row.studentDays}</td>
                  <td className="text-center font-mono">{row.ricePerStuG} g</td>
                  <td className="text-right font-mono"><b>{qty(row.totalRice)}</b> kg</td>
                  <td className="text-center font-mono">Rs {row.costPerStu.toFixed(2)}</td>
                  <td className="text-right font-mono"><b>{money(row.totalCost)}</b></td>
                </tr>
              ))}
              <tr>
                <th>Grand Total</th>
                <th className="text-center font-mono">{summary.reduce((a, r) => a + r.studentDays, 0)}</th>
                <th className="text-center font-mono">—</th>
                <th className="text-right font-mono">{qty(totals.grandRice)} kg</th>
                <th className="text-center font-mono">—</th>
                <th className="text-right font-mono">{money(totals.grandCost)}</th>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[0.7rem] text-ink-700">
            <b>Formula:</b> Total Rice = (DR Days × Students per class) × per-student rice/day · Total Cost = (DR Days × Students per class) × per-student cost/day. Example — Bal Vatika: 9 DR days × 5 students × 100 g = 4500 g rice; 9 × 5 × Rs 11.15 = Rs 501.75 cooking cost.
          </p>
        </div>
        <table className="report-table w-full">
          <thead>
            <tr>
              <th rowSpan={2}>Date</th>
              <th colSpan={2}>Bal Vatika</th>
              <th colSpan={2}>Primary</th>
              <th colSpan={2}>Upper Primary</th>
              <th colSpan={2}>High School</th>
              <th colSpan={2}>Day Total</th>
            </tr>
            <tr>
              <th>Rice (kg)</th><th>Cost (Rs.)</th>
              <th>Rice (kg)</th><th>Cost (Rs.)</th>
              <th>Rice (kg)</th><th>Cost (Rs.)</th>
              <th>Rice (kg)</th><th>Cost (Rs.)</th>
              <th>Rice (kg)</th><th>Cost (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            {days.length === 0 && (
              <tr><td colSpan={11} className="text-center">No dry-ration days recorded for this quarter.</td></tr>
            )}
            {days.map((d) => (
              <tr key={d.date} data-testid={`dr-row-${d.date}`}>
                <td>{d.date}</td>
                <td>{qty(d.balVatika.rice)}</td><td>{money(d.balVatika.cost)}</td>
                <td>{qty(d.primary.rice)}</td><td>{money(d.primary.cost)}</td>
                <td>{qty(d.upperPrimary.rice)}</td><td>{money(d.upperPrimary.cost)}</td>
                <td>{qty(d.highSchool.rice)}</td><td>{money(d.highSchool.cost)}</td>
                <td>{qty(d.totals.rice)}</td><td>{money(d.totals.cost)}</td>
              </tr>
            ))}
            {days.length > 0 && (
              <tr>
                <th>Grand Total</th>
                <th>{qty(totals.balVatika.rice)}</th><th>{money(totals.balVatika.cost)}</th>
                <th>{qty(totals.primary.rice)}</th><th>{money(totals.primary.cost)}</th>
                <th>{qty(totals.upperPrimary.rice)}</th><th>{money(totals.upperPrimary.cost)}</th>
                <th>{qty(totals.highSchool.rice)}</th><th>{money(totals.highSchool.cost)}</th>
                <th>{qty(totals.grandRice)}</th><th>{money(totals.grandCost)}</th>
              </tr>
            )}
          </tbody>
        </table>
        <div className="rounded border border-black/30 bg-sand-50 px-4 py-3 text-sm leading-6" data-testid="dr-amount-words">
          <p><b>Grand Total Cost (English):</b> {inWordsEN(totals.grandCost)}</p>
          <p className="mt-1"><b>Grand Total Cost (ଓଡ଼ିଆ):</b> {inWordsOR(totals.grandCost)}</p>
          <p className="mt-1"><b>Grand Total Rice:</b> {qty(totals.grandRice)} kg</p>
        </div>
        <p className="text-xs text-ink-700">
          Distribution rule per day: Rice = enrolment × per-student rice (100 g/day Primary group, 150 g/day Upper-Primary group).
          Cost = enrolment × per-student cost (Rs 11.15 Primary group / Rs 14.74 Upper-Primary group).
        </p>
      </div>

      {days.length > 0 && (
        <div className="mt-8 border-t border-dashed border-ink-700 pt-6" data-testid="dr-slips-section">
          <h4 className="font-heading text-lg font-semibold text-ink-900">Distribution Slips</h4>
          <p className="mb-3 text-xs text-ink-700">One A4 slip per dry-ration day (prints on a fresh page) with parent / guardian signature lines.</p>
          {days.map((d) => (
            <DryRationSlip key={`slip-${d.date}`} schoolInfo={schoolInfo} day={d} />
          ))}
        </div>
      )}
    </ReportShell>
  );
}

// ---------------------------------------------------------------------------
// FSA (Food Security Allowance) distribution sheets — one per kind:
//   • FsaRiceDistribution — total rice per student during DR period
//   • FsaCostDistribution — total cooking cost per student during DR period
// Both render in LANDSCAPE A4 via the `.fsa-page` print class. Each lists
// every enrolled student row by class with a Parent Signature column on the
// right edge. Footer signatures: SMC Chairperson (left) · Headmaster (right).
// Since the app does not persist student-level rosters, the table fills the
// maximum class headcount observed across DR days with blank name / father
// rows — the school fills these in by hand or types into the editable cells.
// ---------------------------------------------------------------------------

const CLASS_LABEL_OD = {
  balVatika: "ବାଳ ବାଟିକା",
  primary: "ପ୍ରାଥମିକ",
  upperPrimary: "ଉଚ୍ଚ ପ୍ରାଥମିକ",
  highSchool: "ଉଚ୍ଚ ବିଦ୍ୟାଳୟ",
};
const CLASS_LABEL_EN = {
  balVatika: "Bal Vatika",
  primary: "Primary",
  upperPrimary: "Upper Primary",
  highSchool: "High School",
};

function buildFsaRows(dryRationData, schoolInfo) {
  // Per-student per-day rates fixed by the PM-POSHAN scheme.
  const RATE = {
    balVatika:    { riceG: 100, cost: 11.15 },
    primary:      { riceG: 100, cost: 11.15 },
    upperPrimary: { riceG: 150, cost: 14.74 },
    highSchool:   { riceG: 150, cost: 14.74 },
  };
  const days = dryRationData?.totals?.days || 0;
  const dayList = (dryRationData?.days || []);
  const headcount = (k) => Math.max(0, ...dayList.map((d) => Number(d?.[k]?.count || 0)));
  const cls = ["balVatika", "primary", "upperPrimary", "highSchool"];
  const rows = [];
  let sl = 1;
  cls.forEach((k) => {
    const n = headcount(k);
    if (!n) return;
    const totalRiceKg = (RATE[k].riceG * days * 1) / 1000; // per student (kg)
    const totalCost   = Number((RATE[k].cost * days * 1).toFixed(2));
    for (let i = 0; i < n; i += 1) {
      rows.push({
        sl: sl++,
        className: CLASS_LABEL_EN[k],
        classNameOd: CLASS_LABEL_OD[k],
        studentName: "",
        fatherName: "",
        totalRiceKg,
        totalCost,
      });
    }
  });
  // Fallback — at least 10 blank lines so the printable sheet is not empty
  // when the school hasn't entered DR days yet.
  if (rows.length === 0) {
    for (let i = 0; i < 10; i += 1) rows.push({ sl: i + 1, className: "", classNameOd: "", studentName: "", fatherName: "", totalRiceKg: 0, totalCost: 0 });
  }
  return { rows, days, schoolInfo };
}

function FsaHeader({ schoolInfo, kindLabel }) {
  return (
    <div className="mb-3 border-b-2 border-black pb-2 text-center">
      <p className="text-base font-bold uppercase tracking-wide">{schoolInfo.schoolName || "____________"}</p>
      <p className="text-xs">
        UDISE: <b>{schoolInfo.udiseCode || "______"}</b> · Block: {schoolInfo.block || "______"} · District: {schoolInfo.district || "______"}
      </p>
      <p className="mt-1 text-sm font-semibold">
        {kindLabel} · {schoolInfo.financialYear} · {schoolInfo.quarter}
      </p>
    </div>
  );
}

function FsaSignatureRow() {
  return (
    <div className="mt-12 grid grid-cols-2 gap-12 text-center text-sm">
      <div className="border-t border-black pt-2">
        Signature of SMC / SMDC Chairperson<br/>
        <span className="text-xs">ବିଦ୍ୟାଳୟ ପରିଚାଳନା କମିଟି ସଭାପତି</span>
      </div>
      <div className="border-t border-black pt-2">
        Signature of Headmaster / I.C. HM<br/>
        <span className="text-xs">ପ୍ରଧାନ ଶିକ୍ଷକ</span>
      </div>
    </div>
  );
}

export function FsaRiceDistribution({ schoolInfo, dryRationData }) {
  const { rows, days } = buildFsaRows(dryRationData, schoolInfo);
  return (
    <ReportShell
      title="Dry Rice Distributed under FSA"
      subtitle={`Food Security Allowance · ${days} Dry-Ration day(s)`}
      className="fsa-page"
      testId="fsa-rice-distribution"
    >
      <FsaHeader schoolInfo={schoolInfo} kindLabel="Dry Rice Distribution List (FSA)" />
      <table className="report-table w-full" data-testid="fsa-rice-table">
        <thead>
          <tr>
            <th style={{ width: "5%" }}>SL</th>
            <th style={{ width: "22%" }}>Student Name<br/><span className="text-[0.65rem] font-normal">ଛାତ୍ର/ଛାତ୍ରୀ ନାମ</span></th>
            <th style={{ width: "14%" }}>Class<br/><span className="text-[0.65rem] font-normal">ଶ୍ରେଣୀ</span></th>
            <th style={{ width: "22%" }}>Father / Mother Name<br/><span className="text-[0.65rem] font-normal">ପିତା/ମାତା ନାମ</span></th>
            <th style={{ width: "13%" }}>Total Rice Distributed<br/><span className="text-[0.65rem] font-normal">(kg) · ବଣ୍ଟିତ ଚାଉଳ</span></th>
            <th style={{ width: "24%" }}>Parent Signature<br/><span className="text-[0.65rem] font-normal">ଅଭିଭାବକଙ୍କ ସ୍ୱାକ୍ଷର</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={`rice-${idx}`} data-testid={`fsa-rice-row-${idx}`}>
              <td className="text-center font-mono">{r.sl}</td>
              <td>&nbsp;</td>
              <td>{r.className}{r.classNameOd ? ` (${r.classNameOd})` : ""}</td>
              <td>&nbsp;</td>
              <td className="text-right font-mono">{r.totalRiceKg ? `${qty(r.totalRiceKg)} kg` : ""}</td>
              <td>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
      <FsaSignatureRow />
    </ReportShell>
  );
}

export function FsaCostDistribution({ schoolInfo, dryRationData }) {
  const { rows, days } = buildFsaRows(dryRationData, schoolInfo);
  return (
    <ReportShell
      title="Cooking Cost Distributed under FSA"
      subtitle={`Food Security Allowance · ${days} Dry-Ration day(s)`}
      className="fsa-page"
      testId="fsa-cost-distribution"
    >
      <FsaHeader schoolInfo={schoolInfo} kindLabel="Cooking Cost Distribution List (FSA)" />
      <table className="report-table w-full" data-testid="fsa-cost-table">
        <thead>
          <tr>
            <th style={{ width: "5%" }}>SL</th>
            <th style={{ width: "22%" }}>Student Name<br/><span className="text-[0.65rem] font-normal">ଛାତ୍ର/ଛାତ୍ରୀ ନାମ</span></th>
            <th style={{ width: "14%" }}>Class<br/><span className="text-[0.65rem] font-normal">ଶ୍ରେଣୀ</span></th>
            <th style={{ width: "22%" }}>Father / Mother Name<br/><span className="text-[0.65rem] font-normal">ପିତା/ମାତା ନାମ</span></th>
            <th style={{ width: "13%" }}>Total Cooking Cost<br/><span className="text-[0.65rem] font-normal">(Rs.) · ବଣ୍ଟିତ ରନ୍ଧନ ବ୍ୟୟ</span></th>
            <th style={{ width: "24%" }}>Parent Signature<br/><span className="text-[0.65rem] font-normal">ଅଭିଭାବକଙ୍କ ସ୍ୱାକ୍ଷର</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={`cost-${idx}`} data-testid={`fsa-cost-row-${idx}`}>
              <td className="text-center font-mono">{r.sl}</td>
              <td>&nbsp;</td>
              <td>{r.className}{r.classNameOd ? ` (${r.classNameOd})` : ""}</td>
              <td>&nbsp;</td>
              <td className="text-right font-mono">{r.totalCost ? money(r.totalCost) : ""}</td>
              <td>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
      <FsaSignatureRow />
    </ReportShell>
  );
}
